import { useState } from "react";
import { EmailSchedulesContext } from "@components/preferences";
import BackButton from "@components/back_button.tsx";
import Container from "@components/ui/container.tsx";
import type { EmailSchedule } from "@components/job_alerts/types.ts";
import { JobAlertsHeader, SubscriptionCountProvider } from "@components/job_alerts/header.tsx";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorState } from "@components/job_alerts/error_state.tsx";
import { LoadingState } from "@components/job_alerts/loading_state.tsx";
import { Suspense } from "react";
import { JobAlertsGrid } from "@components/job_alerts/grid.tsx";
import { PausedBanner } from "@components/job_alerts/paused_banner.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";
import { router } from "@inertiajs/react";

type Props = {
  paths: {
    notificationSchedulePath: string;
    emailPreferencesPath: string;
  };
  emailSchedule: EmailSchedule;
  emailSchedules: EmailSchedule[];
  jobAlertsPaused: boolean;
};

const JobAlertsPage = ({ paths, emailSchedule, emailSchedules, jobAlertsPaused }: Props) => {
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [pendingEditPath, setPendingEditPath] = useState<string | null>(null);
  const [pendingCompanyName, setPendingCompanyName] = useState<string>("");

  const handleEditWhilePaused = (editPath: string, companyName: string) => {
    setPendingEditPath(editPath);
    setPendingCompanyName(companyName);
    setShowPausedModal(true);
  };

  const navigateToEdit = () => {
    setShowPausedModal(false);
    if (pendingEditPath) router.visit(pendingEditPath);
  };

  return (
    <Container className="!max-w-[1550px]">
      <EmailSchedulesContext.Provider
        value={{
          emailSchedule,
          emailSchedules,
          notificationSchedulePath: paths.notificationSchedulePath,
        }}
      >
        <ErrorBoundary fallback={<ErrorState />}>
          <Suspense fallback={<LoadingState />}>
            <SubscriptionCountProvider>
              <BackButton />
              <JobAlertsHeader jobAlertsPaused={jobAlertsPaused} />
              {jobAlertsPaused && (
                <PausedBanner emailPreferencesPath={paths.emailPreferencesPath} />
              )}
              <JobAlertsGrid
                onEditWhilePaused={jobAlertsPaused ? handleEditWhilePaused : undefined}
              />
            </SubscriptionCountProvider>
          </Suspense>
        </ErrorBoundary>
      </EmailSchedulesContext.Provider>
      <EnableAlertsModal
        open={showPausedModal}
        onOpenChange={setShowPausedModal}
        companyName={pendingCompanyName}
        emailPreferencesPath={paths.emailPreferencesPath}
        onDismiss={navigateToEdit}
      />
    </Container>
  );
};

export default JobAlertsPage;
